import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { LuArrowRight, LuMegaphone, LuPencilLine, LuSettings } from 'react-icons/lu';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Page, PageContent, PageTitle, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { useCmsPermissions } from '@/views/admin/cms/cms-permissions';
import { cmsHistoryQueryOptions, useCmsMicrosites } from '@/views/admin/cms/cms-queries';
import { deriveCmsVersionRows } from '@/views/admin/cms/cms-version-state';
import { CmsRoutes } from '@/views/admin/cms/CmsRoutes';
import { useAuthContext } from '@/views/auth/Auth';
import { formatDate, titleCase } from 'utils';

import {
    MARKETING_CMS_PATH,
    MARKETING_CMS_SITES_PATH,
    marketingCmsSiteBuilderPath,
    marketingCmsSiteHistoryPath,
} from './marketing-routes';

const MarketingOverview = () => {
    const { user } = useAuthContext();
    const cmsPermissions = useCmsPermissions();
    const { data: microsites = [], isLoading: isLoadingMicrosites } = useCmsMicrosites(user?.profileGUID, {
        enabled: !!user?.profileGUID,
    });
    const selectedMicrosite = microsites.find((microsite) => cmsPermissions.canEditMicrosite(microsite.ownerUserGUID));
    const hasWorkflowPersona = cmsPermissions.canReview || cmsPermissions.canPublish || cmsPermissions.canAdmin;
    const { data: cmsHistory } = useQuery(cmsHistoryQueryOptions(selectedMicrosite?.cmsMicrositeGUID));
    const cmsRows = deriveCmsVersionRows(cmsHistory, selectedMicrosite);
    const publishedRow = cmsRows.find((row) => row.isPublished);

    return (
        <Page>
            <PageTopBar />
            <PageTitleRow>
                <div>
                    <PageTitle>Marketing</PageTitle>
                    <p className="text-cocoa-600 text-sm">Manage advisor marketing channels.</p>
                </div>
            </PageTitleRow>
            <PageContent>
                <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-2">
                    <Card className="border-cocoa-100 min-w-0 bg-white shadow-sm">
                        <CardContent className="flex h-full min-w-0 flex-col p-5">
                            <div className="mb-3 flex min-w-0 items-center gap-3">
                                <div className="bg-cocoa-900 text-cocoa-0 flex size-9 shrink-0 items-center justify-center rounded-md text-base tracking-widest">
                                    <span>C</span>
                                    <span className="text-seafoam-300">A</span>
                                </div>
                                <h2 className="text-cocoa-1000 font-heading truncate text-lg">My Microsite</h2>
                            </div>
                            <p className="font-heading text-cocoa-1000 truncate text-2xl">
                                {selectedMicrosite?.advisorDisplayName ??
                                    selectedMicrosite?.siteName ??
                                    (isLoadingMicrosites ? 'Loading microsite...' : 'No microsite selected')}
                            </p>
                            {selectedMicrosite && (
                                <p className="text-cocoa-600 mt-1 truncate text-sm">
                                    claroadvisors.com/advisors/{selectedMicrosite.slug}
                                </p>
                            )}
                            <div className="text-cocoa-600 mt-5 flex min-w-0 flex-wrap items-center gap-3 text-xs">
                                {selectedMicrosite?.publicStatus && (
                                    <span className="bg-gold-100 text-gold-600 rounded px-3 py-1 font-semibold tracking-wider uppercase">
                                        {titleCase(selectedMicrosite.publicStatus)}
                                    </span>
                                )}
                                {publishedRow && (
                                    <span>
                                        Live: Version {publishedRow.version.versionNumber}
                                        {formatDate(selectedMicrosite?.lastPublishedAt, 'MMM d') &&
                                            ` - ${formatDate(selectedMicrosite?.lastPublishedAt, 'MMM d')}`}
                                    </span>
                                )}
                            </div>
                            <div className="mt-auto flex flex-wrap gap-3 pt-10">
                                <Button
                                    asChild
                                    variant="highlighted"
                                    size="sm"
                                    className="hover:bg-cocoa-700 gap-1.5 px-4 text-base"
                                >
                                    <Link
                                        to={
                                            selectedMicrosite
                                                ? marketingCmsSiteHistoryPath(selectedMicrosite.cmsMicrositeGUID)
                                                : MARKETING_CMS_SITES_PATH
                                        }
                                    >
                                        <LuSettings className="h-4 w-4" />
                                        Manage
                                    </Link>
                                </Button>
                                <Button asChild variant="outline2" size="sm" className="gap-1.5 px-4 text-base">
                                    <Link
                                        to={
                                            selectedMicrosite
                                                ? marketingCmsSiteBuilderPath(selectedMicrosite.cmsMicrositeGUID)
                                                : MARKETING_CMS_SITES_PATH
                                        }
                                    >
                                        <LuPencilLine className="h-4 w-4" />
                                        Edit latest draft
                                    </Link>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                    {hasWorkflowPersona && (
                        <MarketingFeatureCard
                            to={MARKETING_CMS_SITES_PATH}
                            icon={<LuMegaphone />}
                            title="Advisor Microsites"
                            description="Manage advisor microsite drafts, reviews, publishing, and live-site history."
                            cta="Manage microsites"
                        />
                    )}
                </div>
            </PageContent>
        </Page>
    );
};

const MarketingFeatureCard = ({
    to,
    icon,
    title,
    description,
    cta,
}: {
    to: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    cta: string;
}) => (
    <Card className="border-cocoa-100 hover:bg-cocoa-0/60 min-w-0 bg-white shadow-sm transition-colors">
        <Link
            className="focus:ring-seafoam-300 block h-full min-w-0 rounded-lg focus:ring-2 focus:outline-none"
            to={to}
        >
            <CardContent className="flex h-full min-w-0 flex-col p-5">
                <div className="mb-3 flex min-w-0 items-center gap-3">
                    <div className="bg-seafoam-100 text-seafoam-600 flex size-9 shrink-0 items-center justify-center rounded-md">
                        {icon}
                    </div>
                    <h2 className="text-cocoa-1000 font-heading truncate text-lg">{title}</h2>
                </div>
                <p className="text-cocoa-600 text-sm leading-6">{description}</p>
                <span className="text-cocoa-1000 mt-auto flex items-center gap-1 pt-10 text-sm font-[500]">
                    {cta} <LuArrowRight size={14} />
                </span>
            </CardContent>
        </Link>
    </Card>
);

/** Microsite management used to live under /preferences/cms — keep old links working. */
export const PreferencesCmsRedirect = () => {
    const { pathname, search, hash } = useLocation();
    const rest = pathname.replace(/^\/preferences\/cms/i, '');

    return <Navigate to={`${MARKETING_CMS_PATH}${rest}${search}${hash}`} replace />;
};

export const MarketingPage = () => (
    <ErrorBoundary>
        <Routes>
            <Route index element={<MarketingOverview />} />
            <Route path="cms/*" element={<CmsRoutes />} />
        </Routes>
    </ErrorBoundary>
);
